import type { CaseStudy } from '../types';

export const transistor: CaseStudy = {
  id: 'transistor',
  order: 16,
  title: '晶体管与贝尔实验室',
  english: 'The Transistor & Bell Labs',
  category: 'technology',
  era: '1947 — 1970s',
  region: '美国 → 全球',
  accent: '#367a6a',
  hook: '晶体管的伟大不只是“更小的电子管”，而是把材料、制造、专利许可和标准连成了一条能把科学变成产业的路径。',
  question: '为什么一项实验室发明，需要几十年和整套产业生态，才变成改变所有行业的通用技术？',
  summary:
    '1947 年贝尔实验室发明晶体管，随后半导体、集成电路和摩尔定律逐步成熟。科学突破本身不足以形成产业：材料纯度、制造良率、专利许可、设备与标准缺一不可。',
  thesis: '通用技术的扩散，取决于互补资产和制度安排，而不是单次发明的聪明程度。',
  quote: '实验室里做出第一只晶体管只用了几年；让世界用上亿万只，用了整个产业。',
  isNew: true,
  entryNodeIds: ['transistor-science', 'transistor-manufacturing', 'transistor-patents'],
  focusNodeId: 'transistor-patents',
  lensIds: ['standards', 'public-goods', 'scarcity', 'lockin'],
  opportunityIds: ['opp-deep-tech-transfer'],
  sourceIds: ['nobel-transistor', 'bell-labs-history'],
  facts: [
    {
      text: '1947 年，贝尔实验室的约翰·巴丁、沃尔特·布拉顿和威廉·肖克利完成了点接触晶体管的实验；三人获得 1956 年诺贝尔物理学奖。',
      sourceIds: ['nobel-transistor', 'bell-labs-history'],
    },
    {
      text: '晶体管替代了体积大、耗电、易损的真空管，首先在电话、计算机和消费电子中扩散；早期器件昂贵且良率有限。',
      sourceIds: ['bell-labs-history'],
    },
    {
      text: '1956 年美国司法部与 AT&T 的反垄断同意判决要求其广泛许可专利；具体条款与影响需要回到判决文本，不能简化为“专利开放导致了一切”。',
      sourceIds: ['bell-labs-history'],
    },
  ],
  nodes: [
    {
      id: 'transistor',
      caseId: 'transistor',
      kind: 'case',
      stage: 0,
      lane: 0,
      title: '晶体管',
      summary: '从贝尔实验室走向整个电子产业的基础器件。',
      detail:
        '晶体管不是单一发明，而是一组科学、材料与制造工艺的集合。它把弱电信号的放大与控制从笨重的真空管中解放出来，为计算机、通信和消费电子的小型化、可靠性提升与成本下降奠定基础。',
      boundary: '晶体管的商业化并非一家公司、一个专利或一个年份完成；不同器件类型、材料和市场的时间线不同。',
      evidence: 'fact',
      confidence: 'high',
      sourceIds: ['nobel-transistor'],
    },
    {
      id: 'transistor-science',
      caseId: 'transistor',
      kind: 'mechanism',
      stage: 1,
      lane: -1,
      title: '科学突破：理解半导体，才能控制电流',
      summary: '量子力学与固体物理的进展，让工程师可以有意识地设计开关与放大。',
      detail:
        '晶体管依赖对半导体能带、掺杂和载流子的理解。贝尔实验室的团队不是凭运气，而是在固体物理、材料与电子工程的交叉处工作。科学提供了“为什么可以这样控制电流”的解释，工程则把它变成可重复的器件。',
      boundary: '科学理解并不自动带来可制造的产品；材料纯度、工艺稳定性和成本同样是瓶颈。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['nobel-transistor'],
    },
    {
      id: 'transistor-manufacturing',
      caseId: 'transistor',
      kind: 'mechanism',
      stage: 1,
      lane: 1,
      title: '制造：从“能做”到“能重复地做”',
      summary: '提纯、掺杂、光刻、封装和良率控制，决定实验室成果能否量产。',
      detail:
        '早期晶体管对材料缺陷和工艺误差极其敏感。只有把硅/锗提纯、氧化、扩散、光刻和封装变成稳定流程，器件才能可靠、便宜地大规模生产。制造的隐性知识往往比论文更难复制。',
      boundary: '制造能力需要设备、材料、工程师和长期工艺积累；后发者可以通过引进与学习缩短时间，但很难跳过。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['bell-labs-history'],
    },
    {
      id: 'transistor-patents',
      caseId: 'transistor',
      kind: 'mechanism',
      stage: 2,
      lane: -1,
      title: '专利与许可：控制扩散速度',
      summary: '谁拥有核心专利、许可条件如何，决定了技术扩散的速度与产业权力。',
      detail:
        '专利可以保护发明者，也可能成为后来者的进入壁垒。1956 年的反垄断同意判决要求 AT&T 广泛许可专利，降低了其他公司进入半导体的门槛。但许可费、交叉许可和专利组合仍然塑造竞争格局。',
      boundary: '专利制度的具体效果取决于法域、权利要求、执法与交叉许可实践。',
      evidence: 'mechanism',
      confidence: 'medium',
      sourceIds: ['bell-labs-history'],
    },
    {
      id: 'transistor-ecosystem',
      caseId: 'transistor',
      kind: 'mechanism',
      stage: 2,
      lane: 1,
      title: '生态：设备、材料与设计一起成熟',
      summary: '光刻机、化学品、EDA、封装与测试形成互补网络，单点突破才能放大。',
      detail:
        '晶体管产业不是一家工厂，而是一条由设备、材料、设计工具、代工和封装组成的链。每一环都需要投资与学习；当这些互补方共同成熟，设计成本、制造良率和迭代速度才会改善。',
      boundary: '生态越集中，效率越高，也越容易受到地缘、设备或关键材料的单点冲击。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['bell-labs-history'],
    },
    {
      id: 'transistor-condition',
      caseId: 'transistor',
      kind: 'condition',
      stage: 2,
      lane: 0,
      title: '前提：国防、通信与计算机需求',
      summary: '早期高昂的研发与制造成本，需要政府、电信和计算机等大买家支撑。',
      detail:
        '晶体管早期价格高、可靠性待验证，普通消费市场难以独自支撑。国防采购、电话网络和大型计算机提供了初始需求与学习场景。需求规模让企业愿意投资工艺，工艺改善又降低成本。',
      boundary: '不同国家的初始需求结构不同；政府角色与私人市场的组合会影响产业路径。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['bell-labs-history'],
    },
    {
      id: 'transistor-outcome',
      caseId: 'transistor',
      kind: 'outcome',
      stage: 3,
      lane: 0,
      title: '结果：半导体成为通用技术',
      summary: '成本下降与集成度提升，让计算和通信进入更多场景。',
      detail:
        '晶体管与集成电路的成本、体积和功耗持续改善，催生个人电脑、移动通信、互联网和后来的 AI 芯片。通用技术的价值不在于某一件产品，而在于它降低了整个经济中“计算与控制”的成本。',
      boundary: '技术扩散的速度受价格、标准、人才和应用场景影响；不是所有国家都能自动获得产业收益。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['nobel-transistor', 'bell-labs-history'],
    },
    {
      id: 'transistor-risk',
      caseId: 'transistor',
      kind: 'risk',
      stage: 4,
      lane: -1,
      title: '脆弱处：把科学优势当成产业优势',
      summary: '论文领先、专利领先，不等于制造、成本和供应链领先。',
      detail:
        '许多国家在基础研究上很强，却在设备、材料、量产和客户生态上受制。技术优势要变成商业优势，需要把隐性工艺、供应链、资本和客户关系一起建起来。单点领先很容易被系统优势超越。',
      boundary: '在软件或设计等轻资产环节，科学优势更容易直接转化为产品；硬件制造的转化路径更长。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['nobel-transistor', 'bell-labs-history'],
    },
    {
      id: 'transistor-opp',
      caseId: 'transistor',
      kind: 'opportunity',
      stage: 4,
      lane: 1,
      title: '机会透镜：做“从论文到量产”的翻译',
      summary: '深科技创业的稀缺能力，不是再写一篇论文，而是把工艺、认证和客户连起来。',
      detail:
        '机会包括：材料与设备的 specialized 服务、工艺验证、可靠性测试、洁净室工程、技术转移与知识产权运营。对个人，成为懂技术又懂客户与合规的“翻译者”，比单纯做研究更稀缺。',
      boundary: '深科技周期长、资本密集、客户集中；没有长期资金和产业伙伴，很难只靠咨询跨越死亡谷。',
      evidence: 'inference',
      confidence: 'medium',
      sourceIds: ['bell-labs-history'],
    },
  ],
  edges: [
    {
      id: 'transistor-e1',
      caseId: 'transistor',
      from: 'transistor',
      to: 'transistor-science',
      label: '科学基础',
      explanation: '半导体物理与材料科学让可控的电流开关成为可能。',
      tone: 'cause',
    },
    {
      id: 'transistor-e2',
      caseId: 'transistor',
      from: 'transistor',
      to: 'transistor-manufacturing',
      label: '从样品到产品',
      explanation: '只有工艺可重复、良率可控，实验室器件才能变成商品。',
      tone: 'cause',
    },
    {
      id: 'transistor-e3',
      caseId: 'transistor',
      from: 'transistor-science',
      to: 'transistor-patents',
      label: '知识变成产权',
      explanation: '科学发现通过专利与许可进入商业竞争。',
      tone: 'cause',
    },
    {
      id: 'transistor-e4',
      caseId: 'transistor',
      from: 'transistor-manufacturing',
      to: 'transistor-ecosystem',
      label: '工艺需要互补方',
      explanation: '设备、材料、封装和测试共同决定制造能力。',
      tone: 'enable',
    },
    {
      id: 'transistor-e5',
      caseId: 'transistor',
      from: 'transistor-patents',
      to: 'transistor-ecosystem',
      label: '许可影响进入',
      explanation: '专利许可条件改变后来者能否进入与围绕谁形成生态。',
      tone: 'cause',
    },
    {
      id: 'transistor-e6',
      caseId: 'transistor',
      from: 'transistor-manufacturing',
      to: 'transistor-outcome',
      label: '成本与良率下降',
      explanation: '可重复制造让晶体管价格下降、可靠性提高，进入更多应用。',
      tone: 'cause',
    },
    {
      id: 'transistor-e7',
      caseId: 'transistor',
      from: 'transistor-ecosystem',
      to: 'transistor-outcome',
      label: '系统优势',
      explanation: '互补方共同成熟，使整个产业的迭代速度和设计能力提升。',
      tone: 'cause',
    },
    {
      id: 'transistor-e8',
      caseId: 'transistor',
      from: 'transistor-condition',
      to: 'transistor-outcome',
      label: '早期需求支撑学习',
      explanation: '国防、电信和计算机订单提供了规模与试错场景。',
      tone: 'enable',
    },
    {
      id: 'transistor-e9',
      caseId: 'transistor',
      from: 'transistor-outcome',
      to: 'transistor-risk',
      label: '系统优势压倒单点',
      explanation: '制造、供应链与客户生态的短板，会吞掉论文和专利的领先。',
      tone: 'risk',
    },
    {
      id: 'transistor-e10',
      caseId: 'transistor',
      from: 'transistor-risk',
      to: 'transistor-opp',
      label: '转化能力稀缺',
      explanation: '把科学变成量产需要跨学科、跨组织的翻译能力。',
      tone: 'enable',
    },
  ],
};
